// ============================================
// Toast global discreto (Snackbar bottom-center) — fila leve, um por vez.
// Consumido por qualquer parte do app via useToast(); alimenta os avisos do
// stream ao vivo (nova excursão) sem acoplar ao componente que os dispara.
// Padrão de fila do MUI: só promove o próximo depois que o atual sai da tela.
// ============================================

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import Snackbar from '@mui/material/Snackbar'
import Alert, { type AlertColor } from '@mui/material/Alert'

interface ToastOptions {
  severity?: AlertColor
  /** ms até fechar sozinho; `null` = fica até ser fechado à mão. Default: 5000, ou persistente para erros. */
  duration?: number | null
}

interface ToastItem {
  key: number
  message: ReactNode
  severity: AlertColor
  duration: number | null
}

interface ToastContextValue {
  /** Enfileira um toast. Retorna imediatamente. */
  showToast: (message: ReactNode, opts?: ToastOptions) => void
  /** Atalho: erro persistente (não some sozinho — precisa ser lido e fechado). */
  showError: (message: ReactNode) => void
  /** Atalho: sucesso (some em 4s). */
  showSuccess: (message: ReactNode) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [queue, setQueue] = useState<ToastItem[]>([])
  const [current, setCurrent] = useState<ToastItem | null>(null)
  const [open, setOpen] = useState(false)
  const seq = useRef(0)

  const showToast = useCallback((message: ReactNode, opts: ToastOptions = {}) => {
    const severity = opts.severity ?? 'info'
    // Erro persiste por padrão (não pode evaporar antes de ser lido); demais 5s.
    const duration = opts.duration !== undefined
      ? opts.duration
      : (severity === 'error' ? null : 5000)
    setQueue((q) => [...q, { key: seq.current++, message, severity, duration }])
  }, [])

  const showError = useCallback((message: ReactNode) => {
    showToast(message, { severity: 'error', duration: null })
  }, [showToast])

  const showSuccess = useCallback((message: ReactNode) => {
    showToast(message, { severity: 'success', duration: 4000 })
  }, [showToast])

  // Promove o próximo da fila assim que nada está na tela.
  useEffect(() => {
    if (current == null && queue.length > 0) {
      setCurrent(queue[0])
      setQueue((q) => q.slice(1))
      setOpen(true)
    }
  }, [queue, current])

  const handleClose = useCallback((_e: unknown, reason?: string) => {
    if (reason === 'clickaway') return
    setOpen(false)
  }, [])

  // onExited: item saiu da tela — libera o slot para o próximo da fila.
  const handleExited = useCallback(() => {
    setCurrent(null)
  }, [])

  const value = useMemo<ToastContextValue>(() => ({ showToast, showError, showSuccess }), [showToast, showError, showSuccess])

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Snackbar
        key={current?.key}
        open={open}
        autoHideDuration={current?.duration ?? 5000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        TransitionProps={{ onExited: handleExited }}
      >
        {current != null
          ? (
            // Fundo `main` + `contrastText`: no tema escuro o filled do MUI usa
            // o tom `.dark` com texto calculado sobre o `.main` (3,5–4,0:1, reprova AA).
            <Alert
              severity={current.severity}
              variant="filled"
              onClose={() => setOpen(false)}
              sx={{ boxShadow: 6, bgcolor: `${current.severity}.main`, color: `${current.severity}.contrastText` }}
            >
              {current.message}
            </Alert>
            )
          : undefined}
      </Snackbar>
    </ToastContext.Provider>
  )
}

export const useToast = (): ToastContextValue => {
  const ctx = useContext(ToastContext)
  if (ctx == null) throw new Error('useToast deve ser usado dentro de <ToastProvider>')
  return ctx
}
